import { Volume2, VolumeX } from 'lucide-react';
import { useSound } from '@/contexts/SoundContext';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from './tooltip';

interface SoundToggleProps {
  className?: string;
  showLabel?: boolean;
}

export function SoundToggle({ className, showLabel = false }: SoundToggleProps) {
  const { isMuted, toggleMute, playSound } = useSound();

  const handleToggle = () => {
    toggleMute();
    // Play a sound when unmuting to confirm
    if (isMuted) {
      setTimeout(() => playSound('click'), 50);
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <motion.button
          onClick={handleToggle}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={cn(
            "flex items-center gap-2 px-2 py-1.5 rounded-lg",
            "text-muted-foreground hover:text-foreground",
            "hover:bg-muted/50 transition-colors duration-200",
            className
          )}
          aria-label={isMuted ? "Unmute sounds" : "Mute sounds"}
        >
          <motion.div
            initial={false}
            animate={{ rotate: isMuted ? 0 : 360 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            {isMuted ? (
              <VolumeX className="h-4 w-4" />
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
          </motion.div>
          {showLabel && (
            <span className="text-xs font-medium">
              {isMuted ? "Sound Off" : "Sound On"}
            </span>
          )}
        </motion.button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>{isMuted ? "Enable UI sounds" : "Disable UI sounds"}</p>
      </TooltipContent>
    </Tooltip>
  );
}